import { useState, useRef } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { FileXls, Clipboard, DownloadSimple, UploadSimple, MapPin, Funnel, Lock } from '@phosphor-icons/react';

/**
 * Indane Gas Data Extractor
 *
 * Helper utility used by Indane distributor staff to pull bulk consumer data
 * out of an Excel sheet (or pasted text) and download a 2-column workbook
 * (Mobile Number, LPG ID) for use in the booking workspace.
 *
 * RBAC: visible to admin/owner/staff/sub_staff/assistant only — explicitly
 * NOT shown to ca/accountant (handled in App.js route guard + Sidebar visibility).
 *
 * Originally a standalone HTML tool the user provided; ported to React so it
 * lives inside the app and inherits auth/sidebar/UX consistency.
 */
export default function IndaneExtractorPage() {
  // Defense-in-depth role check (sidebar already hides for unauthorized roles).
  // Allowed: admin / owner / staff / sub_staff / assistant.
  // Explicitly DENIED: ca / accountant.
  const { user } = useAuth();
  const ALLOWED = ['admin', 'owner', 'staff', 'sub_staff', 'assistant'];

  const [mode, setMode] = useState('excel');                  // 'excel' | 'paste'
  const [fileName, setFileName] = useState('');
  const [allData, setAllData] = useState([]);                 // parsed rows (with area)
  const [areaOptions, setAreaOptions] = useState([]);
  const [selectedArea, setSelectedArea] = useState('all');
  const [excelDaysFilter, setExcelDaysFilter] = useState('none'); // '26' | '50' | 'none'
  const [excelQuantity, setExcelQuantity] = useState('');
  const [readyData, setReadyData] = useState([]);             // post-filter, ready to download

  // Quick paste state
  const [agencyName, setAgencyName] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [pasteDaysFilter, setPasteDaysFilter] = useState('none');
  const [pasteQuantity, setPasteQuantity] = useState('');
  const fileInputRef = useRef(null);

  if (user && !ALLOWED.includes(user.role)) {
    return (
      <div className="max-w-md mx-auto mt-12 p-6 bg-red-50 border border-red-200 rounded-lg text-center">
        <Lock size={32} className="mx-auto text-red-600 mb-2" weight="duotone" />
        <p className="font-bold text-red-800">Access Denied</p>
        <p className="text-sm text-red-700 mt-1">
          Indane Data Extractor sirf staff/sub_staff/assistant/admin/owner ke liye available hai.
          Aapke role ({user.role}) ke liye yeh service enabled nahi hai.
        </p>
      </div>
    );
  }

  const cleanLpgId = (v) => (v == null ? '' : String(v).replace(/\./g, '').trim());

  // ── Excel upload + parse ───────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { header: 1 });
        processExcelData(json);
      } catch (err) {
        toast.error('Excel parse failed: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const processExcelData = (jsonData) => {
    let headerRowIndex = -1;
    for (let i = 0; i < Math.min(10, jsonData.length); i++) {
      const row = jsonData[i];
      if (row && row.some(c => c && String(c).toLowerCase().includes('consumer'))) {
        headerRowIndex = i;
        break;
      }
    }
    if (headerRowIndex === -1) {
      toast.error('Excel me "Consumer" header column nahi mila');
      return;
    }
    const headers = jsonData[headerRowIndex].map(h => (h ? String(h).toLowerCase() : ''));
    const idx = {
      consumer: headers.findIndex(h => h.includes('consumer number') || h.includes('consumer no')),
      lpg: headers.findIndex(h => h.includes('lpg') || h.includes('consumer id')),
      phone: headers.findIndex(h => h.includes('mobile') || h.includes('phone')),
      area: headers.findIndex(h => h.includes('area') || h.includes('city')),
      lastDelivery: headers.findIndex(h => h.includes('last') && (h.includes('delivery') || h.includes('refill'))),
    };
    const rows = [];
    const areas = new Set();
    for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
      const row = jsonData[i];
      if (!row || row.length === 0) continue;
      const consumerNum = idx.consumer !== -1 ? row[idx.consumer] : '';
      const lpgId = idx.lpg !== -1 ? cleanLpgId(row[idx.lpg]) : '';
      const phone = idx.phone !== -1 ? row[idx.phone] : '';
      const area = idx.area !== -1 ? (row[idx.area] || 'Unknown') : 'Unknown';
      const lastDelivery = idx.lastDelivery !== -1 ? row[idx.lastDelivery] : null;
      if (!consumerNum && !lpgId && !phone) continue;
      areas.add(area);
      rows.push({
        consumerNum: consumerNum ? String(consumerNum).trim() : '',
        lpgId,
        phone: phone ? String(phone).trim() : '',
        area,
        lastDelivery,
      });
    }
    setAllData(rows);
    setAreaOptions(Array.from(areas).sort());
    setSelectedArea('all');
    toast.success(`Excel parsed — ${rows.length} rows from ${areas.size} areas`);
  };

  // ── Apply filters and prepare downloadable list ────────────────────────
  const applyFilters = () => {
    let filtered = allData;
    if (selectedArea !== 'all') filtered = filtered.filter(r => r.area === selectedArea);
    if (excelDaysFilter !== 'none') {
      const cutoff = parseInt(excelDaysFilter, 10);
      filtered = filtered.filter(r => {
        if (!r.lastDelivery) return false;
        let dt;
        if (typeof r.lastDelivery === 'number') dt = new Date((r.lastDelivery - 25569) * 86400 * 1000);
        else dt = new Date(r.lastDelivery);
        if (isNaN(dt.getTime())) return false;
        const daysDiff = Math.floor((new Date() - dt) / 86400000);
        return daysDiff > cutoff;
      });
    }
    const ready = filtered.filter(r => r.lpgId && r.phone);
    setReadyData(ready);
    if (ready.length === 0) toast.error('Filters ke baad koi row nahi bachi');
    else toast.success(`Ready to download: ${ready.length} unique records`);
  };

  // ── Download Excel (Mobile + LPG ID) ───────────────────────────────────
  const downloadExcel = (rows, prefix = 'Indane') => {
    if (!rows || rows.length === 0) { toast.error('Download karne ke liye data nahi hai'); return; }
    const qty = mode === 'excel' ? parseInt(excelQuantity || '0', 10) : parseInt(pasteQuantity || '0', 10);
    if (!qty || qty < 1) { toast.error('Records quantity daalein (kitne records chahiye)'); return; }
    const slice = rows.slice(0, qty);
    const wb = XLSX.utils.book_new();
    const wsData = [
      ['Mobile Number', 'LPG ID'],
      ...slice.map(r => [r.phone, r.lpgId]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Indane Data');
    const today = new Date().toISOString().split('T')[0];
    const safeAgency = (agencyName || prefix).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 30);
    XLSX.writeFile(wb, `${safeAgency}_${today}.xlsx`);
    toast.success(`✅ Downloaded ${slice.length} records`);
  };

  // ── Quick Paste mode ───────────────────────────────────────────────────
  const processPaste = () => {
    const txt = pasteText.trim();
    if (!txt) { toast.error('Paste area khali hai'); return; }
    const lines = txt.split('\n').filter(l => l.trim());
    const out = [];
    for (const line of lines) {
      const delim = line.includes('\t') ? '\t' : (/\s{2,}/.test(line) ? /\s{2,}/ : ',');
      const parts = line.split(delim).map(p => p.trim()).filter(p => p);
      if (parts.length < 2) continue;
      const consumerNum = parts[0] || '';
      const lpgId = cleanLpgId(parts[1] || '');
      const phone = parts[2] || '';
      const lastDelivery = parts[3] || null;
      if (pasteDaysFilter !== 'none' && lastDelivery) {
        const dt = new Date(lastDelivery);
        if (!isNaN(dt.getTime())) {
          const daysDiff = Math.floor((new Date() - dt) / 86400000);
          if (daysDiff <= parseInt(pasteDaysFilter, 10)) continue;
        }
      }
      if (lpgId && phone) out.push({ consumerNum, lpgId, phone });
    }
    if (out.length === 0) { toast.error('Filter ke baad koi valid row nahi bachi'); return; }
    downloadExcel(out, agencyName || 'Indane Agency');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileXls size={24} weight="duotone" className="text-violet-600" />
          Indane Gas Data Extractor
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Sare Indane distributors ke liye — bulk consumer data extract karke booking workspace ke liye CSV/Excel ready karein.
        </p>
      </div>

      {/* Mode Selection */}
      <div className="rounded-xl p-5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white">
        <h2 className="font-semibold mb-3 text-center">📁 Processing Mode Select karein</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={() => setMode('excel')}
            className={`bg-white text-violet-700 font-bold rounded-lg py-3 px-4 transition-all ${mode === 'excel' ? 'ring-4 ring-yellow-300 -translate-y-0.5 shadow-lg' : 'opacity-70 hover:opacity-100'}`}
            data-testid="mode-excel-btn"
          >
            📊 Excel Upload (Full Features)
          </button>
          <button
            onClick={() => setMode('paste')}
            className={`bg-white text-violet-700 font-bold rounded-lg py-3 px-4 transition-all ${mode === 'paste' ? 'ring-4 ring-yellow-300 -translate-y-0.5 shadow-lg' : 'opacity-70 hover:opacity-100'}`}
            data-testid="mode-paste-btn"
          >
            📋 Quick Paste (Fast & Simple)
          </button>
        </div>
      </div>

      {/* Excel Upload Mode */}
      {mode === 'excel' && (
        <Card data-testid="excel-mode-section">
          <CardContent className="p-6 space-y-5">
            <h2 className="text-lg font-bold text-emerald-700 flex items-center gap-2">
              <UploadSimple size={20} /> Excel Upload Mode
            </h2>
            <div className="rounded-xl border-2 border-dashed border-violet-300 bg-violet-50/40 p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="indane-file-input"
                data-testid="indane-file-input"
              />
              <label htmlFor="indane-file-input" className="inline-block bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-lg py-3 px-8 cursor-pointer hover:translate-y-[-2px] hover:shadow-lg transition-all">
                📁 Excel File Upload karein
              </label>
              {fileName && <div className="mt-3 text-sm text-emerald-700 font-semibold">✅ {fileName}</div>}
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <MapPin size={14} className="text-violet-600" /> Area Select karein (Optional)
              </label>
              <select
                value={selectedArea}
                onChange={(e) => setSelectedArea(e.target.value)}
                className="w-full h-10 mt-1 rounded-md border border-gray-300 px-3 bg-white text-sm"
                data-testid="indane-area-select"
              >
                <option value="all">🌍 All Areas</option>
                {areaOptions.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <Funnel size={14} className="text-amber-600" /> Last Delivery Filter (Optional)
              </label>
              <div className="flex gap-4 mt-2">
                {[
                  { v: '26', label: '26 Days Old' },
                  { v: '50', label: '50 Days Old' },
                  { v: 'none', label: 'No Filter' },
                ].map(o => (
                  <label key={o.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="excel-days"
                      value={o.v}
                      checked={excelDaysFilter === o.v}
                      onChange={(e) => setExcelDaysFilter(e.target.value)}
                      data-testid={`excel-days-${o.v}`}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Records Quantity</label>
              <Input
                type="number"
                min="1"
                value={excelQuantity}
                onChange={(e) => setExcelQuantity(e.target.value)}
                placeholder="e.g., 100"
                className="h-10 text-sm mt-1"
                data-testid="indane-excel-qty"
              />
              <div className="text-xs text-amber-600 mt-1">💡 Kitne unique records download karna hai</div>
            </div>

            <div className="flex gap-3 flex-wrap">
              <Button
                onClick={applyFilters}
                disabled={allData.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="indane-apply-filters-btn"
              >
                <Funnel size={16} className="mr-1" /> Apply Filters
              </Button>
              <Button
                onClick={() => downloadExcel(readyData)}
                disabled={readyData.length === 0}
                className="bg-gradient-to-r from-fuchsia-500 to-rose-500 hover:from-fuchsia-600 hover:to-rose-600"
                data-testid="indane-download-btn"
              >
                <DownloadSimple size={16} className="mr-1" />
                Download Excel ({readyData.length} ready)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Paste Mode */}
      {mode === 'paste' && (
        <Card data-testid="paste-mode-section">
          <CardContent className="p-6 space-y-5">
            <h2 className="text-lg font-bold text-emerald-700 flex items-center gap-2">
              <Clipboard size={20} /> Quick Paste Mode
            </h2>

            <div>
              <label className="text-sm font-semibold text-gray-700">Agency Name (Optional)</label>
              <Input
                value={agencyName}
                onChange={(e) => setAgencyName(e.target.value)}
                placeholder="Khali chhodne par 'Indane Agency' use hoga"
                className="h-10 text-sm mt-1"
                data-testid="indane-agency-name"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <Funnel size={14} className="text-amber-600" /> Last Delivery Filter (Optional)
              </label>
              <div className="flex gap-4 mt-2">
                {[
                  { v: '26', label: '26 Days Old' },
                  { v: '50', label: '50 Days Old' },
                  { v: 'none', label: 'No Filter' },
                ].map(o => (
                  <label key={o.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="paste-days"
                      value={o.v}
                      checked={pasteDaysFilter === o.v}
                      onChange={(e) => setPasteDaysFilter(e.target.value)}
                      data-testid={`paste-days-${o.v}`}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Booking Data Yahan Paste karein</label>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={`📋 Excel se booking data yahan paste karein

Format: Consumer Number, LPG ID, Mobile Number, Last Delivery Date (optional)

Example:
41570222    1000000041570222    9319821973    Mar 20, 2026
42345142    1000000042345142    8218709365    Jan 15, 2026`}
                className="w-full mt-1 min-h-[260px] p-3 border-2 border-dashed border-emerald-400 rounded-lg font-mono text-sm focus:outline-none focus:border-emerald-600"
                data-testid="indane-paste-area"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-gray-700">Records Quantity</label>
              <Input
                type="number"
                min="1"
                value={pasteQuantity}
                onChange={(e) => setPasteQuantity(e.target.value)}
                placeholder="e.g., 100"
                className="h-10 text-sm mt-1"
                data-testid="indane-paste-qty"
              />
            </div>

            <Button
              onClick={processPaste}
              className="w-full bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-600 hover:to-teal-500 text-white font-bold py-3"
              data-testid="indane-paste-process-btn"
            >
              🚀 Process & Download
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
